import type { QueryClient } from "@tanstack/react-query";

/**
 * The root segment of each entity's query keys, in one place.
 *
 * Why this exists: a mutation only refreshes the keys of its OWN entity, but
 * plenty of screens show one entity's data about another — the option-type list
 * shows "Used in N presets", a preset card shows its option types' names, the
 * header shows the store's name from the auth profile, a category card shows
 * how many products it holds. Without a cross-entity refresh those stay stale
 * until a reload, and one of them guards a delete (a stale "used in 0 presets"
 * let a protected option type be deleted).
 *
 * Entities cannot import each other (FSD, same layer), so the shared layer
 * owns the names. `app/__tests__/queryRoots.test.ts` fails if an entity's real
 * `all` key ever drifts from its root here — a silent mismatch would invalidate
 * nothing and look exactly like success.
 */
export const QUERY_ROOTS = {
  presets: "presets",
  optionTypes: "option-types",
  /** Admin-side twins of the two above — separate caches, same coupling. */
  systemPresets: "system-presets",
  systemOptionTypes: "system-option-types",
  variants: "variants",
  products: "products",
  categories: "categories",
  materials: "materials",
  featuredSections: "featured-sections",
  dashboardHome: "dashboard-home",
  /** The signed-in user's profile — also where the header reads the STORE from. */
  authProfile: "auth-profile",
} as const;

export type QueryRoot = (typeof QUERY_ROOTS)[keyof typeof QUERY_ROOTS];

/**
 * Refresh every query under the given entities' roots (React Query matches by
 * prefix). Call from a mutation's `onSuccess` for the entities that DISPLAY the
 * mutated entity's data.
 */
export function invalidateRoots(queryClient: QueryClient, ...roots: QueryRoot[]): void {
  for (const root of roots) {
    queryClient.invalidateQueries({ queryKey: [root] });
  }
}
